import { useState, useEffect, useCallback, useRef } from 'preact/hooks';
import { OnboardingIntro } from './components/OnboardingIntro';
import { BootstrapSteps } from './views/BootstrapSteps';
import { MainDashboard } from './views/MainDashboard';
import { ChatView } from './views/ChatView';
import { BillingPanel, BillingDialog } from './components/BillingPanel';
import { SquareLocationPicker } from './components/SquareLocationPicker';
import { GuidedSetup, AgreementStep, SquareRecovery, squareReturnFailure } from './components/GuidedSetup';
import { apiFetch, ApiError } from './api/client';
import { ASSISTANT_NAME } from './brand.js';

/**
 * App - Owner Dashboard Root Component
 *
 * Routes based on onboarding_state:
 * - needs_square: OnboardingIntro (connect Square CTA)
 * - needs_payment: BillingPanel (subscription before paid AI setup)
 * - bootstrapping: BootstrapSteps (progress)
 * - ready + onboarding_chat_complete=false: ChatView (first-run owner
 *     interview — the "I'm Jonathan, here's what I learned from your menu,
 *     now tell me how you run things" moment, full-screen BEFORE the
 *     dashboard; bead jonathan-bxgw)
 * - ready: MainDashboard (Today/Guests/Calendar/Your salon)
 *
 * Philosophy: Conversation is the product. The dashboard is just receipts.
 */
const MAX_BOOTSTRAP_POLLS = 60; // ~3 minutes at 3s intervals

export function App() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const dashboardSnapshot = useRef(null);
  const [refreshError, setRefreshError] = useState(null);
  const [pollingExpired, setPollingExpired] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [squareFailure, setSquareFailure] = useState(() => { const hint = new URLSearchParams(window.location.search).get('square'); return squareReturnFailure(hint) ? hint : null; });
  const [choosingLocation, setChoosingLocation] = useState(false);
  const bootstrapPolls = useRef(0);
  const pollTimer = useRef(null);
  const requestPending = useRef(false);
  const refreshQueued = useRef(false);
  const mounted = useRef(false);
  // First-run interview (bead jonathan-bxgw): once the owner finishes (or the
  // backend flips onboarding_chat_complete), fall through to the dashboard for
  // the rest of this session even if the next dashboard refetch lags. Cleared
  // only by a full reload, which by then reads the persisted complete flag.
  const [interviewDone, setInterviewDone] = useState(false);
  const [setupBillingOpen, setSetupBillingOpen] = useState(false);
  const [billingOpen, setBillingOpen] = useState(() => ['processing', 'canceled'].includes(new URLSearchParams(window.location.search).get('billing')));
  // Only the initially opened dialog handles the Stripe return hint. The
  // inline payment panel and later dialog visits only read server state.
  const [reconcileBillingReturn, setReconcileBillingReturn] = useState(() => new URLSearchParams(window.location.search).get('billing') === 'processing');

  useEffect(() => {
    if (dashboardData?.onboarding_state && dashboardData.onboarding_state !== 'ready') setBillingOpen(false);
  }, [dashboardData?.onboarding_state]);

  // Fetch dashboard data on mount
  useEffect(() => {
    mounted.current = true;
    fetchDashboardData();
    const resume = () => fetchDashboardData(true);
    window.addEventListener('focus', resume); window.addEventListener('pageshow', resume);
    return () => { mounted.current = false; clearTimeout(pollTimer.current); window.removeEventListener('focus', resume); window.removeEventListener('pageshow', resume); };
  }, []);

  const fetchDashboardData = useCallback(async (isRefresh = false) => {
    if (requestPending.current) { if (isRefresh) refreshQueued.current = true; return; }
    requestPending.current = true;
    clearTimeout(pollTimer.current);
    setRefreshing(isRefresh);
    try {
      // Only show loading spinner on initial load, not refreshes
      if (!isRefresh) {
        setLoading(true);
      }

      // Shared client handles credentials + 401 -> /account/login.
      const data = await apiFetch('/v1/owner/dashboard');
      if (!mounted.current) return;
      setError(null);
      setRefreshError(null);
      dashboardSnapshot.current = data;
      setDashboardData(data);

      if (!isRefresh) {
        setLoading(false);
      }

      // Poll while bootstrapping — steps may start as pending before
      // transitioning to in_progress, so poll regardless of step status.
      // Safety valve: stop after MAX_BOOTSTRAP_POLLS to avoid infinite polling.
      if (data.onboarding_state === 'bootstrapping') {
        bootstrapPolls.current += 1;
        const expired = bootstrapPolls.current >= MAX_BOOTSTRAP_POLLS;
        setPollingExpired(expired);
        if (!expired) pollTimer.current = setTimeout(() => fetchDashboardData(true), 3000);
      } else {
        bootstrapPolls.current = 0;
        setPollingExpired(false);
      }
    } catch (err) {
      if (!mounted.current) return;
      // ApiError with status 0 = network failure; any non-401 status lands
      // here too (401 already redirected inside the client).
      console.error('Dashboard fetch error:', err);
      const message = err instanceof ApiError ? err.message : 'Failed to fetch dashboard data';
      if (isRefresh && dashboardSnapshot.current && err instanceof ApiError && (err.status === 0 || err.status >= 500)) {
        setRefreshError('Status could not be refreshed. Your current work is still here.');
      } else {
        dashboardSnapshot.current = null;
        setDashboardData(null);
        setRefreshError(null);
        setError(message);
      }
      setLoading(false);
    } finally {
      requestPending.current = false;
      if (mounted.current) setRefreshing(false);
      if (mounted.current && refreshQueued.current) {
        refreshQueued.current = false;
        queueMicrotask(() => { if (mounted.current) fetchDashboardData(true); });
      }
    }
  }, []);

  const refreshSetup = () => {
    if (requestPending.current) return;
    bootstrapPolls.current = 0;
    setPollingExpired(false);
    fetchDashboardData(true);
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div class="min-h-screen flex flex-col items-center justify-center" style={{ background: 'var(--cream)' }}>
          <div class="text-center">
            <div class="logo text-4xl font-medium mb-4" style={{ color: 'var(--black)' }}>{ASSISTANT_NAME}</div>
            <div class="w-8 h-8 border-2 border-gray-300 rounded-full animate-spin mx-auto" style={{ borderTopColor: 'var(--sage)' }} />
          </div>
        </div>
      );
    }

    if (error) {
      return (
        <div class="min-h-screen flex items-center justify-center p-4" style={{ background: 'var(--cream)' }}>
          <div class="text-center">
            <div style={{ color: 'var(--coral)' }} class="mb-2">Something went wrong</div>
            <button
              onClick={() => fetchDashboardData()}
              style={{ color: 'var(--sage)', textDecoration: 'underline', background: 'none', border: 'none', cursor: 'pointer' }}
            >
              Try again
            </button>
          </div>
        </div>
      );
    }

    const state = dashboardData?.onboarding_state || 'needs_square';

    if (state === 'needs_agreement') return <GuidedSetup step={1}><AgreementStep onUpdated={() => fetchDashboardData(true)} /></GuidedSetup>;

    if (squareFailure && state !== 'ready') return <GuidedSetup step={2}><SquareRecovery hint={squareFailure} onRecovered={() => { setSquareFailure(null); const url = new URL(window.location.href); url.searchParams.delete('square'); window.history.replaceState({}, '', url); fetchDashboardData(true); }} /></GuidedSetup>;

    if (state === 'needs_square') return <GuidedSetup step={2}><OnboardingIntro /></GuidedSetup>;

    if (state === 'needs_location' || choosingLocation) return <GuidedSetup step={3}><SquareLocationPicker guided onConfirmed={() => { setChoosingLocation(false); fetchDashboardData(true); }} />{choosingLocation && <button class="setup-text-button" onClick={() => setChoosingLocation(false)}>Back to subscription</button>}</GuidedSetup>;

    if (state === 'needs_payment') return <GuidedSetup step={4}><BillingPanel needsPayment guided onConnectionIssue={() => setSquareFailure('unavailable')} autoReconcile={reconcileBillingReturn} onUpdated={() => { setReconcileBillingReturn(false); fetchDashboardData(true); }} /><button class="setup-text-button" onClick={() => setChoosingLocation(true)}>Back to location</button></GuidedSetup>;

    if (state === 'bootstrapping') {
      return (
        <GuidedSetup step={5}><BootstrapSteps guided
          salonName={dashboardData?.salon_name}
          steps={dashboardData?.onboarding_steps || {}}
          currentStep={dashboardData?.current_step}
          onRefresh={refreshSetup}
          refreshing={refreshing}
          pollingExpired={pollingExpired}
        /><button class="setup-text-button" onClick={() => { setReconcileBillingReturn(false); setSetupBillingOpen(true); setBillingOpen(true); }}>Manage subscription</button></GuidedSetup>
      );
    }

    // First-run owner interview: state is 'ready' but the owner has never been
    // through the teaching conversation. Show it full-screen before the
    // dashboard. On completion ('Done for now'), re-fetch the dashboard and fall
    // through to MainDashboard.
    if (dashboardData?.ai_available !== false && !dashboardData?.onboarding_chat_complete && !interviewDone) {
      return (
        <ChatView
          dashboardData={dashboardData}
          onComplete={() => {
            setInterviewDone(true);
            fetchDashboardData(true);
          }}
        />
      );
    }

    return (
      <MainDashboard dashboardData={dashboardData} onBilling={() => setBillingOpen(true)} onDataRefresh={() => fetchDashboardData(true)} />
    );
  };

  return <>
    {refreshError && <div role="status" class="dashboard-refresh-notice">{refreshError} <button type="button" disabled={refreshing} onClick={() => fetchDashboardData(true)}>Refresh status</button></div>}
    {renderContent()}
    {dashboardData?.onboarding_state === 'ready' && <button type="button" onClick={() => setBillingOpen(true)} style={{ position: 'fixed', bottom: 'calc(88px + var(--safe-area-bottom, 0px))', right: '1rem', zIndex: 90, minHeight: '44px', padding: '0.5rem 0.875rem', borderRadius: '24px', border: '1px solid var(--border-subtle)', background: 'white', color: 'var(--black)', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', cursor: 'pointer' }}>Billing</button>}
    {billingOpen && (dashboardData?.onboarding_state === 'ready' || (dashboardData?.onboarding_state === 'bootstrapping' && setupBillingOpen)) && <BillingDialog autoReconcile={reconcileBillingReturn} onClose={() => { setReconcileBillingReturn(false); setSetupBillingOpen(false); setBillingOpen(false); }} onUpdated={() => fetchDashboardData(true)} />}
  </>;
}
